// Mock data for Tatenda's marketing portfolio
// Replace any field with real content — structure stays the same.

export const profile = {
  firstName: "Tatenda",
  lastName: "",
  role: "Marketing Graduate",
  tagline:
    "Brand storytelling, consumer insight and campaign craft — translating research into measurable growth.",
  location: "Available globally · Open to relocate",
  email: "tatenda.marketing@example.com",
  phone: "+1 (555) 014‑8829",
  linkedin: "https://linkedin.com/in/tatenda",
  instagram: "https://instagram.com/tatenda.marketing",
  portrait:
    "https://images.unsplash.com/photo-1724690336308-02af024b76ab?w=900&auto=format&fit=crop",
  summary:
    "I'm a recent marketing graduate with a strong appetite for the intersection of culture, data and design. My academic work and internships have centred on translating consumer behaviour into brand strategy — from qualitative research and segmentation, to copy, content systems and paid social. I move between the spreadsheet and the moodboard with equal comfort.",
  resumeUrl: "#",
};

export const navItems = [
  { id: "work", label: "Work" },
  { id: "showreel", label: "Showreel" },
  { id: "about", label: "About" },
  { id: "skills", label: "Skills" },
  { id: "experience", label: "Experience" },
  { id: "contact", label: "Contact" },
];

export const skills = [
  {
    group: "Strategy",
    items: [
      "Brand Positioning",
      "Go‑to‑Market Planning",
      "Competitive Analysis",
      "Customer Segmentation",
      "Value Proposition Design",
    ],
  },
  {
    group: "Research & Analytics",
    items: [
      "Qualitative Interviews",
      "Survey Design",
      "Google Analytics 4",
      "A/B Testing",
      "SPSS · Excel",
    ],
  },
  {
    group: "Content & Social",
    items: [
      "Editorial Calendars",
      "Copywriting",
      "Instagram · TikTok · LinkedIn",
      "Influencer Outreach",
      "SEO Fundamentals",
    ],
  },
  {
    group: "Tools",
    items: [
      "Figma",
      "Canva",
      "HubSpot",
      "Meta Ads Manager",
      "Notion · Airtable",
    ],
  },
];

export const projects = [
  {
    id: 1,
    title: "Re‑introducing a heritage tea brand to Gen Z",
    client: "Capstone · Brand Strategy",
    year: "2025",
    cover:
      "https://images.unsplash.com/photo-1598113082891-dcb7b2032b28?w=1400&auto=format&fit=crop",
    tags: ["Brand Strategy", "Consumer Insight", "Social"],
    summary:
      "A 12‑week capstone repositioning a 60‑year‑old tea brand around 'slow rituals' — combining ethnographic interviews, a category audit and a launch campaign concept.",
    metrics: [
      { label: "Interviews", value: "24" },
      { label: "Concept Lift", value: "+38%" },
      { label: "Deliverables", value: "6" },
    ],
  },
  {
    id: 2,
    title: "Launching a campus mental‑health app on TikTok",
    client: "Internship · Social Campaign",
    year: "2024",
    cover:
      "https://images.pexels.com/photos/8084481/pexels-photo-8084481.jpeg?w=1400&auto=format&fit=crop",
    tags: ["Paid Social", "Content", "Growth"],
    summary:
      "Owned a four‑week organic + paid sprint targeting first‑year students. Built creator briefs, edited short‑form video and ran weekly performance reviews.",
    metrics: [
      { label: "Sign‑ups", value: "3.4k" },
      { label: "CPA", value: "−42%" },
      { label: "Watch‑through", value: "71%" },
    ],
  },
  {
    id: 3,
    title: "Consumer insight study: the ‘quiet luxury’ shopper",
    client: "Independent · Research",
    year: "2024",
    cover:
      "https://images.unsplash.com/photo-1503551723145-6c040742065b?w=1400&auto=format&fit=crop",
    tags: ["Research", "Segmentation", "Report"],
    summary:
      "Mixed‑methods study (n=312 survey + 9 interviews) mapping how 22–30 year olds reconcile aspiration and restraint in apparel buying. Findings published as a 28‑page editorial report.",
    metrics: [
      { label: "Respondents", value: "312" },
      { label: "Segments", value: "4" },
      { label: "Pages", value: "28" },
    ],
  },
  {
    id: 4,
    title: "Editorial content system for a boutique studio",
    client: "Freelance · Content Strategy",
    year: "2023",
    cover:
      "https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=1400&auto=format&fit=crop",
    tags: ["Content", "SEO", "Brand Voice"],
    summary:
      "Built a quarterly content calendar, voice guidelines and a 14‑article SEO roadmap for a design studio's relaunched journal — now a primary lead source.",
    metrics: [
      { label: "Organic Traffic", value: "3×" },
      { label: "Articles", value: "14" },
      { label: "Avg. Time", value: "4:12" },
    ],
  },
];

export const experience = [
  {
    role: "Marketing Intern",
    company: "Northlight Studio",
    period: "Jun 2024 — Dec 2024",
    location: "Hybrid",
    bullets: [
      "Owned the editorial calendar across Instagram, LinkedIn and the company journal.",
      "Briefed and edited 11 short‑form video pieces with two external creators.",
      "Built a weekly performance dashboard tracking CTR, CPA and watch‑through.",
    ],
  },
  {
    role: "Brand & Research Assistant",
    company: "University Innovation Lab",
    period: "Sep 2023 — May 2024",
    location: "On‑campus",
    bullets: [
      "Ran 18 user interviews for two student‑founded ventures, synthesised into journey maps.",
      "Drafted positioning statements and tested taglines via guerrilla concept tests.",
      "Co‑authored a 40‑page brand playbook used by the lab's incoming cohort.",
    ],
  },
  {
    role: "Social Media Coordinator (Volunteer)",
    company: "Local Arts Festival",
    period: "Mar 2023 — Aug 2023",
    location: "On‑site",
    bullets: [
      "Grew Instagram following 2.6× in 5 months through editorial recaps and creator partnerships.",
      "Scheduled 80+ posts and managed community DMs during the festival run.",
    ],
  },
];

export const education = [
  {
    school: "University of Cape Town",
    degree: "Bachelor of Commerce — Marketing (Honours track)",
    period: "2021 — 2025",
    detail:
      "Coursework: Consumer Behaviour, Marketing Research, Brand Management, Digital Strategy, Statistics. Capstone awarded distinction.",
  },
  {
    school: "Google Digital Garage",
    degree: "Fundamentals of Digital Marketing",
    period: "2023",
    detail: "Certificate · 26 modules covering SEO, SEM, analytics and content.",
  },
  {
    school: "HubSpot Academy",
    degree: "Inbound Marketing Certification",
    period: "2024",
    detail: "Funnels, lifecycle stages, lead nurturing and CRM hygiene.",
  },
];

export const stats = [
  { value: "4+", label: "Years studying marketing" },
  { value: "12", label: "Live campaigns shipped" },
  { value: "50+", label: "User interviews conducted" },
  { value: "3×", label: "Avg. organic uplift delivered" },
];
